/**
 * branding.js — Central branding registry
 *
 * Controls all visible brand identity on the login page.
 * Switch brands by setting VITE_BRAND in your .env file.
 *
 * Supported values:
 *   VITE_BRAND=aidas      → AIDAS native branding (default)
 *   VITE_BRAND=annexmed   → AnnexMed branding
 */

const BRANDS = {
    aidas: {
        // Identity
        name: 'AIDAS Intelligence Platform',
        signInSubtext: 'Sign in to your AIDAS account',

        // Logo — used on desktop left panel AND mobile header
        logoSrc: '/aidas_logo.png',
        logoAlt: 'AIDAS Logo',

        // Favicon
        faviconSrc: '/favicon-32x32.png',
        collapsedLogoSrc: '/favicon-32x32.png',

        // Mobile fallback: if null, the <img> logo is shown on mobile too.
        // If set, renders a styled text logo instead.
        mobileLogoText: null,

        // Left panel gradient (top → bottom)
        panelGradient: 'linear-gradient(135deg, #0a1628 0%, #0d2240 40%, #1a3a6b 70%, #2563eb 100%)',

        // Accent color used for highlights, glow, button gradients
        accentColor: '#2563eb',
        accentColorLight: '#60a5fa',
        decorativeGlow: 'rgba(37, 99, 235, 0.35)',

        // Sign-in button gradient on the right panel
        buttonGradient: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
        buttonGradientHover: '#1d4ed8',

        // Left panel hero copy
        headline: [' AIDAS Intelligence Platform'],
        accentHeadline: 'Analytics Platform',  // the colored span
        subhead:
            'Empowering organizations with AI-driven data intelligence, automated pipelines, and real-time insights.',

        // Feature bullets on the left panel
        features: [
            {
                icon: '🔒',
                title: 'Secure & Compliant',
                desc: 'Enterprise-grade security with role-based access control',
            },
            {
                icon: '⚡',
                title: 'AI-Powered Pipelines',
                desc: 'Automate complex workflows with intelligent orchestration',
            },
            {
                icon: '📊',
                title: 'Real-time Analytics',
                desc: 'Monitor performance and track KPIs across your organization',
            },
        ],

        // Footer inside left panel
        copyright: 'AIDAS',
        copyrightUrl: 'https://www.aidastech.com/',

        // "Powered by …" link in the right panel footer
        poweredByLabel: 'AIDAS',
        poweredByUrl: 'https://www.aidastech.com/',
    },

    annexmed: {
        // Identity
        name: 'AnnexMed Intelligence Platform',
        signInSubtext: 'Sign in to your AnnexMed account',

        // Logo
        logoSrc: '/annexmed_logo.png',
        logoAlt: 'AnnexMed Logo',

        // Favicon
        faviconSrc: '/annexmed_favicon.png',
        collapsedLogoSrc: '/anexmed_collapsed.png',

        // Mobile fallback: renders styled "Annex/Med" text
        mobileLogoText: { prefix: 'Annex', suffix: 'Med' },

        // Left panel gradient
        panelGradient:
            'linear-gradient(135deg, #0a3d36 0%, #0e6b5f 40%, #1CAB9E 70%, #28c4b5 100%)',

        // Accent
        accentColor: '#1CAB9E',
        accentColorLight: '#4ADE80',
        decorativeGlow: 'rgba(74, 222, 128, 0.35)',

        // Sign-in button
        buttonGradient: 'linear-gradient(135deg, #0e6b5f, #1CAB9E)',
        buttonGradientHover: '#0e6b5f',

        // Hero copy
        headline: [' AnnexMed Intelligence Platform'],
        accentHeadline: 'Revenue Cycle Management',
        subhead:
            'Empowering healthcare organizations with AI-driven solutions for coding accuracy, compliance, and operational excellence.',

        // Features
        features: [
            {
                icon: '🔒',
                title: 'Secure & Compliant',
                desc: 'HIPAA-compliant platform with enterprise-grade security',
            },
            {
                icon: '⚡',
                title: 'AI-Powered Insights',
                desc: 'Leverage intelligent automation for faster, accurate results',
            },
            {
                icon: '📊',
                title: 'Real-time Analytics',
                desc: 'Monitor performance and track KPIs across your organization',
            },
        ],

        // Footer
        copyright: 'AnnexMed',
        copyrightUrl: 'https://www.annexmed.com/',

        // "Powered by …" — AnnexMed is built on AIDAS; keep showing AIDAS here.
        poweredByLabel: 'AIDAS',
        poweredByUrl: 'https://www.aidastech.com/',
    },
};

/**
 * Resolve the active brand from the VITE_BRAND environment variable.
 * Falls back to 'aidas' if the variable is unset or has an unknown value.
 */
const brandKey = (import.meta.env.VITE_BRAND || 'aidas').toLowerCase().trim();
export const BRAND = BRANDS[brandKey] ?? BRANDS.aidas;

export default BRAND;
